/**
 * The view-side cache. Two jobs, both from BUILD-SPEC 8's budgets:
 *
 * **`summary.json` lives in IndexedDB.** Open→glance under a second means the
 * last published summary renders before the network answers - so the cached
 * copy is delivered first, then revalidated with the ETag the server already
 * content-addresses. A 304 costs nothing; a 200 replaces the cache and the
 * screen. Invalidation is therefore explicit and exact: the cache changes
 * precisely when a publish changes the artifact's hash.
 *
 * **Photos go through the Cache API.** They are the sole network exception to
 * "everything metadata-shaped is local", immutable by construction (a capture's
 * photo never changes under its id), and the 30 most recent are prefetched so
 * receipt detail opens with its photo already on disk.
 *
 * The service worker deliberately caches neither (see sw.js): IndexedDB owns
 * data, this module owns photos, and a second opinion in the SW would be two
 * caches with different ideas of the truth.
 */
import { SummarySchema, type Summary } from "./contracts/index.js";

const DB_NAME = "grocery-view";
const DB_VERSION = 1;
const KV = "kv";
const PHOTO_CACHE = "photos-v1";

export interface SummaryFetchResult {
  status: "ok" | "unchanged" | "none";
  summary?: Summary;
  etag?: string | null;
}

export type SummaryFetcher = (etag: string | null) => Promise<SummaryFetchResult>;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(KV)) {
        req.result.createObjectStore(KV, { keyPath: "key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function kv<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(KV, mode);
        const req = fn(t.objectStore(KV));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      }),
  );
}

interface CachedRow {
  key: "summary";
  etag: string | null;
  stored_at: number;
  data: unknown;
}

export async function cachedSummary(): Promise<{ summary: Summary; etag: string | null } | null> {
  const row = await kv<CachedRow | undefined>("readonly", (s) => s.get("summary"));
  if (!row) return null;
  // Validated on the way out as well as the way in: a contract bump between
  // app versions must fail loudly here, not render half a screen.
  const parsed = SummarySchema.safeParse(row.data);
  if (!parsed.success) {
    await clearSummary();
    return null;
  }
  return { summary: parsed.data, etag: row.etag };
}

export async function saveSummary(summary: Summary, etag: string | null): Promise<void> {
  const row: CachedRow = { key: "summary", etag, stored_at: Date.now(), data: summary };
  await kv("readwrite", (s) => s.put(row));
}

export async function clearSummary(): Promise<void> {
  await kv("readwrite", (s) => s.delete("summary"));
}

/**
 * Cache first, network second, in that order on purpose.
 *
 * The listener fires with the cached copy immediately (if there is one), then
 * again only if the network produced something different. Offline is not an
 * error: the cached copy has already been delivered, and the revalidation
 * simply didn't happen.
 */
export async function loadSummary(
  fetcher: SummaryFetcher,
  onUpdate: (summary: Summary | null, source: "cache" | "network") => void,
): Promise<void> {
  const cached = await cachedSummary().catch(() => null);
  if (cached) onUpdate(cached.summary, "cache");
  try {
    const res = await fetcher(cached?.etag ?? null);
    if (res.status === "ok" && res.summary) {
      await saveSummary(res.summary, res.etag ?? null).catch(() => undefined);
      onUpdate(res.summary, "network");
    } else if (res.status === "none") {
      // The server has nothing published. An honest empty state beats a stale
      // cache pretending otherwise.
      await clearSummary().catch(() => undefined);
      onUpdate(null, "network");
    }
    // "unchanged": the cached copy stands, nothing to do.
  } catch {
    if (!cached) onUpdate(null, "cache");
  }
}

// ------------------------------------------------------------------ photos ---

function cacheAvailable(): boolean {
  return typeof caches !== "undefined";
}

function photoRequestUrl(captureId: string): string {
  return `/api/captures/${encodeURIComponent(captureId)}/photo`;
}

/**
 * A photo as a Blob, from the Cache API when it has one.
 *
 * Photos are immutable under their capture id, so a cache hit never needs
 * revalidating - `Cache-Control: private, max-age=31536000` on the server says
 * the same thing.
 */
export async function photoBlob(
  captureId: string,
  fetchBlob: (captureId: string) => Promise<Blob>,
): Promise<Blob> {
  const url = photoRequestUrl(captureId);
  if (cacheAvailable()) {
    const cache = await caches.open(PHOTO_CACHE);
    const hit = await cache.match(url);
    if (hit) return hit.blob();
    const blob = await fetchBlob(captureId);
    await cache.put(url, new Response(blob)).catch(() => undefined);
    return blob;
  }
  return fetchBlob(captureId);
}

/**
 * Warm the cache for the most recent photos, quietly.
 *
 * Fired after a summary lands, never awaited by a screen. Failures are
 * swallowed one by one: a photo that cannot be prefetched is fetched on demand
 * later, and a prune (photo removed after processing) is a normal state, not
 * an error worth surfacing here.
 */
export async function prefetchPhotos(
  captureIds: string[],
  fetchBlob: (captureId: string) => Promise<Blob>,
  limit = 30,
): Promise<void> {
  if (!cacheAvailable()) return;
  const cache = await caches.open(PHOTO_CACHE);
  for (const id of captureIds.slice(0, limit)) {
    const url = photoRequestUrl(id);
    if (await cache.match(url)) continue;
    try {
      const blob = await fetchBlob(id);
      await cache.put(url, new Response(blob));
    } catch {
      /* on-demand later, or pruned; either way not this function's problem */
    }
  }
}
